/**
 * What else belongs to a case: the other mails of its thread, further copies of the
 * same mail, and the quotations in D365 that the mail or the reply of sales names.
 *
 * `ThreadBanners` sits under the page title (a copy points at the first copy, a reply
 * at the first mail of its thread). `RelatedMailsCard` lists the thread and the
 * copies; `QuotationsCard` lists the linked quotations with their status in D365.
 * `ColleagueNotes` shows a colleague's reply that I2Q filed on this case (D1).
 *
 * Read-only display. Every related mail comes from the same mailbox as the case, so a
 * link here never leads outside what the reader may see.
 */

import { Alert, Badge, Card, Typography } from "@norican/react";
import { Link } from "react-router";

import {
  type CaseQuotation,
  type ColleagueNumber,
  type QuoteFollowUp,
  type RelatedCase,
} from "@/lib/api";
import { alsoReceivedText, caseStatusOf } from "@/lib/case-status";
import { toCaseState } from "@/lib/data";
import { formatDateTime } from "@/lib/format";
import { type QuoteCaseDetail } from "@/lib/mock";
import { COLLEAGUE_NOTE_WORDS, ORDER_FOLLOW_UP_TEXT, priceQuestionText } from "@/lib/sales-flow";
import { wonValueText } from "@/lib/work-labels";

function CaseLink({ id, children }: { id: string; children?: React.ReactNode }) {
  return (
    <Link
      to={`/case/${encodeURIComponent(id)}`}
      className="text-accent-fg-subtle font-semibold hover:underline"
    >
      {children ?? id}
    </Link>
  );
}

// ── banners under the title ──────────────────────────────────────────────────

/** "more times" wording for the copies of a mail. */
function timesWord(count: number): string {
  return count === 1 ? "1 more time" : `${count} more times`;
}

/**
 * The banner text of a follow-up on our quotation (sim360 P34). A price question or a
 * discount request (hunt round 2, decision 7) names every quotation it asks about and
 * gets no lines. "No new lines" stands only above an empty draft (hunt round 2, Q-28 c).
 */
export function followUpText(followUp: QuoteFollowUp, hasLines = false): string {
  if (followUp.kind === "price") {
    const numbers = followUp.numbers?.length ? followUp.numbers : [followUp.number];
    return `${priceQuestionText(numbers)}.`;
  }
  // Decision 1 of 14 Oct 2026: an order or a signed copy of our quotation gives its
  // positions as lines, each a suggestion.
  if (followUp.kind === "order") {
    return `${followUp.sentence}. ${ORDER_FOLLOW_UP_TEXT}`;
  }
  const tail =
    followUp.kind === "question"
      ? hasLines
        ? ""
        : " No new lines: reply to the question."
      : followUp.kind === "amend"
        ? " The lines show the quotation with the asked changes."
        : "";
  return `${followUp.sentence}.${tail}`;
}

/** True when the draft has a line to show (a removed request or a stand-in is not one). */
function hasDraftLines(detail: Pick<QuoteCaseDetail, "lines">): boolean {
  return (detail.lines ?? []).some((line) => !line.fallback && line.requestAction !== "remove");
}

/**
 * The thread position in one sentence under the subject (the minimal case page): a
 * follow-up on our quotation, a chaser, or the same inquiry as another case. Null when
 * the mail is a first mail on its own.
 */
export function threadSentence(
  detail: Pick<
    QuoteCaseDetail,
    "quoteFollowUp" | "chaser" | "nearDuplicateOf" | "nearDuplicateText"
  > &
    Partial<Pick<QuoteCaseDetail, "lines">>,
): { text: string; caseId: string | null } | null {
  const followUp = detail.quoteFollowUp ?? null;
  if (followUp) {
    return {
      text: `Follow-up on our quotation ${followUp.number}. ${followUpText(followUp, hasDraftLines({ lines: detail.lines ?? [] }))}`,
      caseId: followUp.original_case_id ?? null,
    };
  }
  if (detail.nearDuplicateOf) {
    return {
      text: detail.nearDuplicateText ?? `Same inquiry as ${detail.nearDuplicateOf}`,
      caseId: detail.nearDuplicateOf,
    };
  }
  if (detail.chaser) {
    return { text: "The customer asks for a reply.", caseId: null };
  }
  return null;
}

/** One line under the subject: follow-up, chaser or "Same inquiry as RFQ-x" (with a link). */
export function ThreadLine({ detail }: { detail: QuoteCaseDetail }) {
  const sentence = threadSentence(detail);
  if (!sentence) {
    return null;
  }
  return (
    <Typography variant="body-sm" className="text-fg break-words" data-thread-line>
      {sentence.text}
      {sentence.caseId && (
        <>
          {" "}
          <CaseLink id={sentence.caseId}>Open {sentence.caseId}</CaseLink>
        </>
      )}
    </Typography>
  );
}

export function ThreadBanners({ detail }: { detail: QuoteCaseDetail }) {
  const copies =
    (detail.related ?? []).filter((r) => r.relation === "copy").length ||
    (detail.duplicateCount ?? 0);
  const followUp = detail.quoteFollowUp ?? null;
  if (
    !detail.duplicateOf &&
    !detail.followUpOf &&
    copies === 0 &&
    !detail.nearDuplicateOf &&
    !followUp
  ) {
    return null;
  }
  return (
    <div className="flex flex-col gap-2">
      {detail.duplicateOf && (
        <Alert tone="neutral" title="Copy of another mail">
          This mail is a copy of <CaseLink id={detail.duplicateOf} />. It reached the mailbox again
          through a colleague&apos;s inbox rule.
        </Alert>
      )}
      {!detail.duplicateOf && copies > 0 && (
        <Alert tone="neutral">
          Also received {timesWord(copies)} through a colleague&apos;s inbox rule.
        </Alert>
      )}
      {detail.nearDuplicateOf && (
        <Alert tone="neutral" title="Same inquiry as another case">
          {detail.nearDuplicateText ?? `Same inquiry as ${detail.nearDuplicateOf}`}. Work on{" "}
          <CaseLink id={detail.nearDuplicateOf} />, so the customer gets one answer.
        </Alert>
      )}
      {followUp && (
        <Alert tone="info" title={`Follow-up on our quotation ${followUp.number}`}>
          {followUpText(followUp, hasDraftLines(detail))}
          {followUp.original_case_id && (
            <>
              {" "}
              Quoted on <CaseLink id={followUp.original_case_id} />.
            </>
          )}
        </Alert>
      )}
      {detail.followUpOf && detail.followUpOf !== followUp?.original_case_id && (
        <Alert tone="info">
          Reply in a thread. First mail: <CaseLink id={detail.followUpOf} />
        </Alert>
      )}
    </div>
  );
}

// ── colleagues' replies filed on the case ────────────────────────────────────

/** What one number of a colleague's reply became on this case. */
export function colleagueNumberText(number: ColleagueNumber): string {
  const what = number.item
    ? [number.item, number.name].filter(Boolean).join(" ")
    : COLLEAGUE_NOTE_WORDS.unknown;
  const where = number.line
    ? COLLEAGUE_NOTE_WORDS.offered(number.line)
    : number.item
      ? COLLEAGUE_NOTE_WORDS.context
      : null;
  return [`${number.written}: ${what}`, where].filter(Boolean).join(". ");
}

/**
 * Decision D1 (19 Oct 2026): a colleague's reply in the thread of this open case, filed
 * here instead of as a case of its own. It shows who replied, when, the colleague's own
 * words and what each item number became: a candidate on an open line, or context.
 * Nothing here decides a line.
 */
export function ColleagueNotes({ detail }: { detail: Pick<QuoteCaseDetail, "colleagueNotes"> }) {
  const notes = detail.colleagueNotes ?? [];
  if (notes.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-2" data-colleague-notes={notes.length}>
      {notes.map((note) => (
        <Alert
          key={note.case_id}
          tone="info"
          title={COLLEAGUE_NOTE_WORDS.title(note.sender_name ?? note.sender_email)}
        >
          <span className="flex flex-col gap-1">
            {note.received_at && (
              <span className="text-fg-muted text-xs">{formatDateTime(note.received_at)}</span>
            )}
            {note.excerpt && (
              <span className="break-words whitespace-pre-line">{note.excerpt}</span>
            )}
            {(note.numbers ?? []).length > 0 && (
              <ul className="flex list-disc flex-col gap-0.5 pl-5 text-xs">
                {(note.numbers ?? []).map((number) => (
                  <li
                    key={number.written}
                    className="break-words"
                    data-colleague-number={number.written}
                  >
                    {colleagueNumberText(number)}
                  </li>
                ))}
              </ul>
            )}
            <span className="text-fg-subtle text-xs">{COLLEAGUE_NOTE_WORDS.filed}</span>
            <span className="text-xs">
              <CaseLink id={note.case_id}>{COLLEAGUE_NOTE_WORDS.open}</CaseLink>
            </span>
          </span>
        </Alert>
      ))}
    </div>
  );
}

// ── related mails ────────────────────────────────────────────────────────────

const RELATION_LABEL: Record<RelatedCase["relation"], string> = {
  first_mail: "First mail",
  reply: "Reply",
  copy: "Copy",
  copy_of: "First copy",
  other_mailbox: "Same mail",
  same_inquiry: "Same inquiry",
  same_inquiry_as: "First mail of this inquiry",
};

/**
 * The thread and the copies of this mail in its mailbox, then the same mail received in
 * another mailbox of the viewer's scope ("Also received in ..."). The server lists only
 * mailboxes the viewer may see, so nothing of another area shows here.
 */
export function RelatedMailsCard({
  related,
  className,
}: {
  related: RelatedCase[];
  className?: string;
}) {
  if (related.length === 0) {
    return null;
  }
  const here = related.filter((mail) => mail.relation !== "other_mailbox");
  const elsewhere = related.filter((mail) => mail.relation === "other_mailbox");
  return (
    <Card variant="outline" padding="none" className={className}>
      <Card.Header className="border-border-subtle flex items-center gap-2 border-b" padding="sm">
        <Typography variant="label">Related mails</Typography>
        <Typography variant="caption" tone="subtle" className="ml-auto">
          {[
            here.length > 0 ? `${here.length} in this mailbox` : "",
            elsewhere.length > 0 ? `${elsewhere.length} in another mailbox` : "",
          ]
            .filter(Boolean)
            .join(", ")}
        </Typography>
      </Card.Header>
      <Card.Body>
        <ul className="flex flex-col gap-2.5">
          {[...here, ...elsewhere].map((mail) => {
            const status = caseStatusOf({ state: toCaseState(mail.state), route: mail.route });
            const sender = mail.sender_name || mail.sender_email || "unknown sender";
            const other = mail.relation === "other_mailbox";
            return (
              <li key={mail.id} className="flex min-w-0 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Badge tone={other ? "info" : "neutral"} variant="outline" size="xs">
                    {RELATION_LABEL[mail.relation]}
                  </Badge>
                  <Typography as="span" variant="caption" tone="subtle">
                    {[formatDateTime(mail.received_at), sender].filter(Boolean).join(" · ")}
                  </Typography>
                  <Badge tone={status.tone} variant="subtle" size="xs">
                    {status.label}
                  </Badge>
                </span>
                {other && (
                  <Typography as="span" variant="caption" className="text-fg break-words">
                    {alsoReceivedText(mail.mailbox ? [mail.mailbox] : [])}
                  </Typography>
                )}
                <span className="min-w-0 text-sm break-words">
                  <CaseLink id={mail.id}>{mail.subject?.trim() || "(no subject)"}</CaseLink>
                </span>
              </li>
            );
          })}
        </ul>
      </Card.Body>
    </Card>
  );
}

// ── quotations in D365 ───────────────────────────────────────────────────────

const SOURCE_TEXT: Record<CaseQuotation["source"], string> = {
  subject: "number in the subject",
  body: "number in the mail text",
  po_reference: "matched by the customer's PO number",
  sent_document: "sent by sales",
  fo_contact: "found in F&O for this sender",
  follow_up: "the customer sent it back",
};

const STATUS_TONE: Record<CaseQuotation["status"], "success" | "info" | "neutral" | "warning"> = {
  confirmed: "success",
  sent: "info",
  created: "neutral",
  lost: "warning",
  cancelled: "neutral",
  unknown: "neutral",
};

/** The status of one quotation as a sentence part, e.g. "confirmed, sales order 690001". */
function statusText(quotation: CaseQuotation): string {
  switch (quotation.status) {
    case "confirmed":
      return quotation.sales_order
        ? `confirmed, sales order ${quotation.sales_order}`
        : "confirmed";
    case "created":
      return "created, not sent yet";
    case "sent":
    case "lost":
    case "cancelled":
      return quotation.status;
    case "unknown":
      return "not known";
  }
}

export function QuotationsCard({
  quotations,
  caseAccount,
  className,
}: {
  quotations: CaseQuotation[];
  /** The customer account of this case, when it is identified with confidence. */
  caseAccount?: string | null;
  className?: string;
}) {
  if (quotations.length === 0) {
    return null;
  }
  return (
    <Card variant="outline" padding="none" className={className}>
      <Card.Header className="border-border-subtle flex items-center gap-2 border-b" padding="sm">
        <Typography variant="label">Quotations in F&amp;O</Typography>
      </Card.Header>
      <Card.Body>
        <ul className="flex flex-col gap-2.5">
          {quotations.map((quotation) => (
            <li key={quotation.number} className="flex min-w-0 flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                <span className="text-fg">
                  Quotation <span className="font-mono font-bold">{quotation.number}</span> (
                  {SOURCE_TEXT[quotation.source]}).
                </span>
                <Badge tone={STATUS_TONE[quotation.status]} variant="subtle" size="sm">
                  Status: {statusText(quotation)}.
                </Badge>
              </span>
              {quotation.won_amount != null && (
                <Typography variant="caption" tone="subtle">
                  Won value: {wonValueText(quotation.won_amount, quotation.currency)}
                </Typography>
              )}
              {quotation.same_customer === false && quotation.customer_account && (
                <Typography variant="caption" className="text-warning-fg">
                  Booked on account {quotation.customer_account}
                  {caseAccount ? `, this case is on ${caseAccount}.` : ", not on this customer."}
                </Typography>
              )}
            </li>
          ))}
        </ul>
      </Card.Body>
    </Card>
  );
}
